import './RefillNotification.css';
import { addPrivateSystemMessage } from '../../slices/chatSlice';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { Tooltip } from '../../components/Tooltips/Tooltip';
import useObjectTranslator from '../../hooks/objectTranslate';
import { Countdown } from '../MultiFightDisplay/Countdown';
import { JImage } from '../../components/UI/JImage';
import { JButton } from '../../components/UI/JButton';
import ImageButton from '../../components/Buttons/ImageButton';
import { Label } from '../../components/UI/Label';
import { CDNImage } from '../../components/Elements/Image';
import { cancelFromServer, fromServer, toServer } from '../../util/ServerSocket';
import { display } from '../../util/EventEmitter';
import Panel from '../../components/Panel/Panel';
import JPanel from '../../components/UI/JPanel';
import { colorChatText } from '../Chat/ColoredChatText';
import { hasExclusiveNeon } from '../Chat/exclusiveName';
import { AttackPowerHud } from './AttackPowerHud';
import { hudStatPower } from './statPower';

function barPercentage(current: number, maximum: number) {
  if (!Number.isFinite(current) || !Number.isFinite(maximum) || maximum <= 0) return 0;
  return Math.max(0, Math.min(100, current / maximum * 100));
}

export function Portrait() {
  const equippedAvatar = useAppSelector((state) => state.inventory.items[state.inventory.locations[300]]);
  const buffs = useAppSelector((state) => state.character.buffs);
  const stats = useAppSelector((state) => state.stats.stats);
  const name = useAppSelector((state) => state.ui.displayName);
  const nameColors = useAppSelector((state) => state.character.nameColors);
  const nameNeon = useAppSelector((state) => state.character.nameNeon);
  const recovery = useAppSelector((state) => state.character.recovery);
  const energy = useAppSelector((state) => state.character.energy);
  const [refilling, setRefilling] = useState(false);
  const dispatch = useAppDispatch();
  const [noticeSuccess, setNoticeSuccess] = useState(false);
  const noticeTimer = useRef<number>();
  const [vpMessage, setVpMessage] = useState('');
  const [confirmRefill, setConfirmRefill] = useState<'HP' | 'MP' | 'VP' | null>(null);
  const busy = useRef(false);
  const vpMax = energy.max ?? 200;
  const vpPercentage = Math.max(0, Math.min(100, energy.current / vpMax * 100));

  useEffect(() => () => window.clearTimeout(noticeTimer.current), []);
  function notify(type: string, success: boolean, reason = '') {
    const message = success ? 'เติม ' + type + ' สำเร็จ' : 'เติม ' + type + ' ไม่สำเร็จ' + (reason ? ' เพราะ' + reason : '');
    setNoticeSuccess(success);
    setVpMessage(message);
    window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setVpMessage(''), 5000);
    const chatMessage = success ? `คุณเติม ${type} สำเร็จ!` : `เสียใจด้วย คุณเติม ${type} ไม่สำเร็จ! กรุณาตรวจสอบยาในกระเป๋า หรือคูปองเพื่อใช้จ่าย`;
    dispatch(addPrivateSystemMessage({ kind: 'system', localNotice: success ? 'success' : 'error', user: 'System', id: 0, serverId: 0, message: chatMessage }));
  }

  function refill(type: 'HP' | 'MP' | 'VP', confirmCoupons = false) {
    if (busy.current) return;
    busy.current = true;
    setRefilling(true);
    setVpMessage('');
    setConfirmRefill(null);
    const timer = window.setTimeout(() => { busy.current = false; setRefilling(false); notify(type, false, 'ไม่ได้รับคำตอบจากเซิร์ฟเวอร์ กรุณาลองอีกครั้ง'); }, 10000);
    toServer(type === 'VP' ? 'refillEnergy' : 'refillVitals', type === 'VP' ? { confirmCoupons } : { type, confirmCoupons }, (result: any) => {
      window.clearTimeout(timer);
      busy.current = false;
      setRefilling(false);
      if (result?.confirmCoupons) setConfirmRefill(type);
      else notify(type, result?.success === true, result?.error?.includes('คูปองไม่พอ') ? 'คูปองไม่พอ' : result?.error || 'เซิร์ฟเวอร์ไม่ตอบรับ');
    });
  }

  useEffect(() => {
    toServer('checkEnergyRefresh');
    const timer = window.setInterval(() => toServer('checkEnergyRefresh'), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const hpPercentage = barPercentage(stats.hp, stats.maxHp);
  const mpPercentage = barPercentage(stats.chakra, stats.maxChakra);
  const hpRecoveryPercentage = barPercentage(recovery.hp, 10000);
  const mpRecoveryPercentage = barPercentage(recovery.chakra, 10000);

  useEffect(() => {
    fromServer('recover', (args) => {});

    return () => {
      cancelFromServer('recover');
    };
  }, []);

  return (
    <div style={{ position: 'absolute', top: 35, display: 'flow' }}>
      <JImage position={{ y: 0 }} src="ui/RoleIcon/bg.png" />
      <JImage
        position={{ y: 0 }}
        src={`icons/people/${equippedAvatar?.props?.avatar}.png`}
        style={{
          WebkitMaskImage: `url(${process.env.REACT_APP_CDN_PATH}ui/RoleIcon/portrait_mask.png)`,
          WebkitMaskRepeat: 'no-repeat',
        }}
      />

      {hasExclusiveNeon(name, nameColors, nameNeon) ? <div style={{ position: 'absolute', left: 92, top: 2, width: 200, fontFamily: 'TrebuchetMS', fontSize: 12 }}>{colorChatText(name, nameColors, nameNeon)}</div> : <Label position={{ x: 92, y: 2 }} text={name} style={{ width: 200, fontFamily: 'TrebuchetMS', fontSize: 12, color: 'whitesmoke' }} />}

      <JImage position={{ x: 73, y: 18 }} src="ui/RoleIcon/indicators.png" />

      <JImage
        position={{ x: 100, y: 18 }}
        src="ui/RoleIcon/hp.png"
        style={{ clipPath: `polygon(0 0, ${hpPercentage}% 0, ${hpPercentage}% 100%, 0 100%)` }}
      />
      <JImage position={{ x: 99, y: 18 }} src="ui/RoleIcon/bar.png" />
      <JImage className="clickable" position={{ x: 204, y: 18 }} src="ui/RoleIcon/refill_box.png" />
      <JImage
        className="clickable"
        position={{ x: 206, y: 19 }}
        src="ui/RoleIcon/refill_hp.png"
        style={{ clipPath: `polygon(0 ${100 - hpRecoveryPercentage}%, 100% ${100 - hpRecoveryPercentage}%, 100% 100%, 0 100%)` }}

      />

      <button aria-label="เติม HP" title="เติม HP ด้วยยา หากไม่มียาใช้ 50 คูปองเติมเต็ม" disabled={refilling || stats.hp >= stats.maxHp} onClick={() => refill('HP')} style={{ position: 'absolute', left: 204, top: 18, width: 15, height: 12, background: 'transparent', border: 0, cursor: 'pointer', zIndex: 1 }} />
      <JImage
        position={{ x: 96, y: 30 }}
        src="ui/RoleIcon/mp.png"
        style={{ clipPath: `polygon(0 0, ${mpPercentage}% 0, ${mpPercentage}% 100%, 0 100%)` }}
      />
      <JImage position={{ x: 95, y: 30 }} src="ui/RoleIcon/bar.png" />
      <JImage position={{ x: 200, y: 30 }} src="ui/RoleIcon/refill_box.png" />
      <JImage
        position={{ x: 202, y: 31 }}
        src="ui/RoleIcon/refill_mp.png"
        style={{ clipPath: `polygon(0 ${100 - mpRecoveryPercentage}%, 100% ${100 - mpRecoveryPercentage}%, 100% 100%, 0 100%)` }}
      />

      <button aria-label="เติม MP" title="เติม MP ด้วยยา หากไม่มียาใช้ 50 คูปองเติมเต็ม" disabled={refilling || stats.chakra >= stats.maxChakra} onClick={() => refill('MP')} style={{ position: 'absolute', left: 200, top: 30, width: 15, height: 12, background: 'transparent', border: 0, cursor: 'pointer', zIndex: 1 }} />
      <JImage
        position={{ x: 92, y: 42 }}
        src="ui/RoleIcon/vp.png"
        style={{ clipPath: `polygon(0 0, ${vpPercentage}% 0, ${vpPercentage}% 100%, 0 100%)` }}
      />
      <JImage position={{ x: 91, y: 42 }} src="ui/RoleIcon/bar.png" />
      <div title={`VP ${energy.current}/${vpMax} · ใช้ยาเติม VP ก่อน หากไม่มียาใช้ 50 คูปองเติมเต็ม`} style={{ position: 'absolute', left: 196, top: 42 }}>
        <ImageButton disabled={refilling || energy.current >= vpMax} defaultImage="ui/RoleIcon/refill_vp_btn.png" onClick={() => refill('VP')} />
      </div>
      {confirmRefill && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 30000, background: 'rgba(0,0,0,0.4)' }} role="dialog" aria-modal="true" aria-label={'ยืนยันเติม ' + confirmRefill}>
          <Panel name={'ยืนยันเติม ' + confirmRefill} moveable={false} onClose={() => setConfirmRefill(null)} style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 30001 }}>
            <JPanel size={{ width: 400, height: 145 }}>
              <div style={{ padding: '24px 16px', textAlign: 'center', lineHeight: '24px', color: '#fff' }}>
                ไม่มียาเติม {confirmRefill} มีค่าใช้จ่าย 50 คูปองเพื่อเติมให้เต็ม<br />ต้องการยืนยันการเติมหรือไม่?
              </div>
              <JButton size={{ width: 100, height: 24 }} position={{ x: 65, y: 110 }} text="ยกเลิก" onClick={() => setConfirmRefill(null)} />
              <JButton size={{ width: 100, height: 24 }} position={{ x: 235, y: 110 }} text="ยืนยัน" onClick={() => refill(confirmRefill, true)} />
            </JPanel>
          </Panel>
        </div>, document.body)}
      {vpMessage && <div role="status" aria-label={vpMessage} title={vpMessage} className="refill-notification">
        <img src={noticeSuccess ? '/assets/ui/notifications/refill-success.png' : '/assets/ui/notifications/refill-failed.png'} alt={vpMessage} />
      </div>}

      <Label
        position={{ x: 82, y: 58 }}
        text={`Lv. ${stats.level}`}
        style={{ width: 100, fontFamily: 'TrebuchetMS', fontSize: 12, color: 'whitesmoke' }}
      />
      <AttackPowerHud power={hudStatPower(stats)}/>

      <div style={{ position: 'absolute', top: 84 }}>
        {Object.keys(buffs).map((id) => (
          <BuffIcon key={id} id={id} expires={buffs[Number(id)]} />
        ))}
      </div>
    </div>
  );
}

export function BuffIcon({ id, expires }: { id: string; expires: number }) {
  const t = useObjectTranslator();
  const [isHover, setIsHover] = useState(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    setNow(Date.now());
    if (!Number.isFinite(expires) || expires <= Date.now()) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [expires]);
  if (!Number.isFinite(expires) || expires <= now) return null;

  return (
    <>
      <CDNImage src={`icons/buffs/${id}.png`} onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)} />
      {isHover && (
        <Tooltip text={t(`buff__${id}--tooltip`)}>
          <Countdown initial={expires} />
        </Tooltip>
      )}
    </>
  );
}

function RecoveryPanel() {
  return (
    <Panel name="Recovery" moveable={false}>
      <JPanel width={300} height={200} background="UIResource.Common.BigBG1"></JPanel>
    </Panel>
  );
}
